import { useState } from 'react';
import type { TFunction } from 'i18next';
import type { useU2000FormStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers';
import type { useU2000GridStateHandlers } from '../../../../u2000Core/reducer/formReducer/U2000GridStateHandlers';
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from '../../../../u2000Core/reducer/formReducer/U2000FormState';
import { generateTemporaryId } from '../../../../u2000Core/tools/U2000IdUtils';
import type { U3202OrgModelConfigGridRow } from '../../../U3202Service';
import { u3202ActivatedModelToGridRow } from '../../../U3202Service';
import type { U3202CatalogModelFormData } from '../../../utils/u3202CatalogModelFormUtils';
import {
  createEmptyU3202CatalogModelFormData,
  u3202CatalogModelFormToActivatedRow,
  u3202GridRowToCatalogModelForm,
} from '../../../utils/u3202CatalogModelFormUtils';

export interface U3202OrgModelConfigHandler {
  onAdd: () => void;
  onModify: () => void;
  onRowClick: (row: U3202OrgModelConfigGridRow) => void;
  onDelete: () => void;
  isDialogOpen: boolean;
  onDlgClose: (result: string) => void;
  isDeleteDialogOpen: boolean;
  onDlgDeleteClose: (result: string) => void;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3202CatalogModelFormData>>;
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3202OrgModelConfigGridRow>>;
  dataChangedEvent: number;
}

export interface U3202OrgModelConfigHandlerParam {
  gridStateHandlers: ReturnType<typeof useU2000GridStateHandlers<U3202OrgModelConfigGridRow>>;
  t: TFunction;
  providerId: string;
  getParentFormState: () => U2000FormStateEnumState;
  formStateHandlers: ReturnType<typeof useU2000FormStateHandlers<U3202CatalogModelFormData>>;
}

export function u3202OrgModelConfigHandler(params: U3202OrgModelConfigHandlerParam): U3202OrgModelConfigHandler {
  const { gridStateHandlers, formStateHandlers, providerId, getParentFormState } = params;
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [dataChangedEvent, setDataChangedEvent] = useState(0);

  const bumpDataChanged = () => setDataChangedEvent((prev) => prev + 1);

  const onAdd = () => {
    formStateHandlers.setData(createEmptyU3202CatalogModelFormData(providerId));
    formStateHandlers.setEditMode(U2000FormStateEnumEditMode.ADD);
    formStateHandlers.setErrors({});
    setDialogOpen(true);
  };

  const isParentReadOnly = () => getParentFormState() === U2000FormStateEnumState.READ;

  const selectSingleRow = (rowId: string) => {
    const targetId = String(rowId);
    const updatedData = gridStateHandlers.getData().map((r) => ({
      ...r,
      idChecked: String(r.id) === targetId,
    }));
    gridStateHandlers.setData(updatedData);
  };

  const openModelDialog = (
    row: U3202OrgModelConfigGridRow,
    editMode: typeof U2000FormStateEnumEditMode.EDIT | typeof U2000FormStateEnumEditMode.NONE,
  ) => {
    const effectiveEditMode = isParentReadOnly() ? U2000FormStateEnumEditMode.NONE : editMode;
    if (isParentReadOnly() || effectiveEditMode === U2000FormStateEnumEditMode.NONE) {
      selectSingleRow(row.id);
    } else {
      gridStateHandlers.selectRow(row.id, true);
    }
    formStateHandlers.setData(u3202GridRowToCatalogModelForm(row, providerId));
    formStateHandlers.setEditMode(effectiveEditMode);
    formStateHandlers.setErrors({});
    setDialogOpen(true);
  };

  const onModify = () => {
    const selectedRow = gridStateHandlers.getSelectedRows()[0] as U3202OrgModelConfigGridRow;
    if (!selectedRow) {
      return;
    }
    openModelDialog(selectedRow, U2000FormStateEnumEditMode.EDIT);
  };

  const onRowClick = (row: U3202OrgModelConfigGridRow) => {
    if (isParentReadOnly()) {
      openModelDialog(row, U2000FormStateEnumEditMode.NONE);
      return;
    }
    openModelDialog(row, U2000FormStateEnumEditMode.EDIT);
  };

  const onDelete = () => setDeleteDialogOpen(true);

  const onDlgClose = (result: string) => {
    if (
      result === 'cancel' ||
      formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.NONE
    ) {
      formStateHandlers.cancelData();
      setDialogOpen(false);
      return;
    }

    const businessHandlers = formStateHandlers.getBusinessHandlers();
    if (businessHandlers?.validateForm) {
      const errors = businessHandlers.validateForm(formStateHandlers.getData());
      formStateHandlers.setErrors(errors);
      if (Object.keys(errors).length > 0) {
        return;
      }
    }

    const form = formStateHandlers.getData();
    const editMode = formStateHandlers.getFormEditMode();

    if (editMode === U2000FormStateEnumEditMode.EDIT) {
      const gridRows = gridStateHandlers.getData();
      const previous = gridRows.find((row) => row.modelId === form.modelId);
      const activatedRow = u3202CatalogModelFormToActivatedRow(form, previous ?? null);
      activatedRow.rowState = previous?.rowState === 'new' ? 'new' : 'modified';
      const updatedGrid = gridRows.map((row) =>
        row.modelId === form.modelId ? u3202ActivatedModelToGridRow(activatedRow) : row,
      );
      gridStateHandlers.setData(updatedGrid);
      bumpDataChanged();
    } else if (editMode === U2000FormStateEnumEditMode.ADD) {
      const activatedRow = u3202CatalogModelFormToActivatedRow({
        ...form,
        modelId: generateTemporaryId(),
      });
      activatedRow.rowState = 'new';
      activatedRow.modelVersion = 0;
      const newGridRow = u3202ActivatedModelToGridRow(activatedRow);
      gridStateHandlers.setData([...gridStateHandlers.getData(), newGridRow]);
      gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
      bumpDataChanged();
    }

    formStateHandlers.afterSaveData(form);
    setDialogOpen(false);
  };

  const onDlgDeleteClose = (result: string) => {
    if (result !== 'yes') {
      setDeleteDialogOpen(false);
      return;
    }

    const selectedRows = gridStateHandlers.getSelectedRows() as U3202OrgModelConfigGridRow[];

    const remainingRows = gridStateHandlers
      .getData()
      .filter((row) => !selectedRows.some((selected) => selected.id === row.id));
    gridStateHandlers.setData(remainingRows);
    gridStateHandlers.setTotalRows(remainingRows.length);

    bumpDataChanged();
    setDeleteDialogOpen(false);
  };

  return {
    onAdd,
    onModify,
    onRowClick,
    onDelete,
    onDlgClose,
    isDialogOpen,
    isDeleteDialogOpen,
    onDlgDeleteClose,
    formStateHandlers,
    gridStateHandlers,
    dataChangedEvent,
  };
}
